import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RolesTableSimple, RoleDetailsDrawer, CreateRoleDrawer, EditRoleDrawer, type Role } from '../../components/security';
import { RolesService } from '../../services/security/roles.service';
import { NotificationToast } from '../../components/ui/NotificationToast';

export const RolesPage: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState<Role | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(7);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activoFilter, setActivoFilter] = useState<boolean | undefined>(undefined);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Estados para notificaciones
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [updateNotificationConfig, setUpdateNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  
  // Cerrar drawer cuando se desmonta el componente
  useEffect(() => {
    return () => {
      setSelectedRole(null);
    };
  }, []);

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Resetear página cuando cambie el término de búsqueda debounced
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, activoFilter]);

  // Cerrar dropdown cuando se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Query para obtener roles
  const {
    data: rolesData,
    isLoading: isLoadingRoles,
    error: rolesError,
    refetch: refetchRoles,
    isFetching: isFetchingRoles
  } = useQuery({
    queryKey: ['roles', page, limit, debouncedSearchTerm, activoFilter],
    queryFn: async () => {
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        return await RolesService.searchRoles(debouncedSearchTerm.trim(), page, limit, activoFilter);
      }
      return await RolesService.getRoles(page, limit, activoFilter);
    },
    staleTime: 30000, // 30 segundos
    refetchOnWindowFocus: false,
    retry: 2
  });

  const roles = rolesData?.data || [];
  const meta = rolesData?.meta;

  // Mantener foco en el input después de la búsqueda
  useEffect(() => {
    if (!isFetchingRoles && searchTerm && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isFetchingRoles, searchTerm]);

  // Handlers
  const handleCreateRole = () => {
    setIsCreateRoleOpen(true);
  };

  const handleCloseCreateRole = () => {
    setIsCreateRoleOpen(false);
  };

  const handleCreateRoleSuccess = () => {
    refetchRoles();
    setIsCreateRoleOpen(false);
  };

  const handleViewRole = (role: Role) => {
    setSelectedRole(role);
  };

  const handleCloseRoleDetails = () => {
    setSelectedRole(null);
  };

  const handleEditRole = (role: Role) => {
    setRoleToEdit(role);
    setSelectedRole(null); // Cerrar drawer de detalles si está abierto
  };

  const handleCloseEditRole = () => {
    setRoleToEdit(null);
  };

  const handleUpdateRoleSuccess = (success: boolean, message: string) => {
    setUpdateNotificationConfig({
      title: success ? '¡Éxito!' : 'Error',
      message,
      type: success ? 'success' : 'error'
    });
    setShowUpdateNotification(true);
    
    if (success) {
      refetchRoles();
      setRoleToEdit(null);
    }
  };

  const handleToggleRoleStatus = async (role: Role) => {
    try {
      const updatedRole = await RolesService.toggleRoleStatus(role.id);
      
      // Actualizar el rol seleccionado si es el mismo que se modificó
      if (selectedRole && selectedRole.id === role.id) {
        setSelectedRole(updatedRole);
      }
      
      // Refrescar la lista
      refetchRoles();
    } catch (error) {
      console.error('Error al cambiar estado del rol:', error);
      throw error; // Re-throw para que el drawer pueda manejarlo
    }
  };

  // Manejo de paginación
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && (!meta || newPage <= meta.totalPages)) {
      setPage(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (meta?.hasPrevious) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (meta?.hasNext) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const handleActivoFilterChange = (activo: boolean | undefined) => {
    setActivoFilter(activo);
  };

  if (rolesError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xs font-medium text-gray-900 mb-4">Error al cargar roles</h2>
          <p className="text-gray-600 mb-4">No se pudieron cargar los roles. Por favor, intenta nuevamente.</p>
          <button 
            onClick={() => refetchRoles()}
            className="bg-purple-600 text-white px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        {/* Filtros y barra de búsqueda */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Filtros de estado */}
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500 font-medium">Filtrar por:</span>
            <div className="flex space-x-1">
              <button
                onClick={() => handleActivoFilterChange(undefined)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  activoFilter === undefined
                    ? 'bg-purple-100 text-purple-700 border-purple-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => handleActivoFilterChange(true)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  activoFilter === true
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Activos
              </button>
              <button
                onClick={() => handleActivoFilterChange(false)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  activoFilter === false
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Inactivos
              </button>
            </div>
          </div>
          
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {isFetchingRoles && searchTerm ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
              ) : (
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar roles por nombre..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-xs placeholder-gray-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200/50 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                <div className="text-xs">
                  {searchTerm ? (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-purple-700">{meta ? meta.total : roles.length}</span>
                      <span className="text-gray-600">resultados encontrados</span>
                      <svg className="w-4 h-4 text-purple-500 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-purple-700">{meta ? meta.total : roles.length}</span>
                      <span className="text-gray-600">roles registrados</span>
                      <svg className="w-4 h-4 text-purple-500 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Selector de cantidad por página */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500 font-medium">Mostrar</span>
              <div className="relative" data-dropdown>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-xs font-medium text-gray-700 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all cursor-pointer flex items-center min-w-15"
                >
                  {limit}
                </button>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                    {[7, 10, 20, 30, 50].map((value) => (
                      <div
                        key={value}
                        onClick={() => {
                          handleLimitChange(value);
                          setIsDropdownOpen(false);
                        }}
                        className="px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <button
              onClick={handleCreateRole}
              className="bg-purple-600 text-white px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Nuevo Rol</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de roles */}
      <div className="relative">
        <RolesTableSimple
          roles={roles}
          isLoading={isLoadingRoles && roles.length === 0}
          onViewRole={handleViewRole}
        />
        {isFetchingRoles && roles.length > 0 && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-6 py-4 flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-600 border-t-transparent"></div>
              <span className="text-xs font-medium text-gray-700">Buscando...</span>
            </div>
          </div>
        )}
      </div>

      {/* Paginación */}
      {meta && (
        <div className="px-6 py-6 border-t border-gray-100 bg-gray-50/30">
          <div className="flex items-center justify-between">
            {/* Información de resultados */}
            <div className="flex items-center space-x-6">
              <div className="text-xs text-gray-600">
                <span className="font-medium text-gray-900">
                  {((meta.page - 1) * meta.limit) + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-gray-900">
                  {Math.min(meta.page * meta.limit, meta.total)}
                </span>
                <span className="mx-2">de</span>
                <span className="font-medium text-gray-900">{meta.total}</span>
                <span className="ml-1">resultados</span>
              </div>
              
              {/* Páginas X de Y */}
              <div className="text-xs text-gray-500">
                Página <span className="font-medium text-gray-900">{meta.page}</span> de <span className="font-medium text-gray-900">{meta.totalPages}</span>
              </div>
            </div>
            
            {/* Controles de paginación */}
            <div className="flex items-center space-x-1">
              {/* Botón primera página */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={!meta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  meta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Primera página"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              {/* Botón página anterior */}
              <button
                onClick={handlePreviousPage}
                disabled={!meta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  meta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página anterior"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Páginas centrales */}
              <div className="flex items-center space-x-1 mx-2">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 3;
                  let start = Math.max(1, meta.page - Math.floor(maxVisiblePages / 2));
                  let end = Math.min(meta.totalPages, start + maxVisiblePages - 1);
                  
                  start = Math.max(1, end - maxVisiblePages + 1);

                  // Mostrar primera página si no está en el rango
                  if (start > 1) {
                    pages.push(
                      <button
                        key="1"
                        onClick={() => handlePageChange(1)}
                        className="w-9 h-9 flex items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        1
                      </button>
                    );
                    if (start > 2) {
                      pages.push(
                        <span key="ellipsis-start" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                  }

                  // Páginas del rango central
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`w-9 h-9 flex items-center justify-center text-xs font-medium rounded-lg transition-all ${
                          i === meta.page
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Mostrar última página si no está en el rango
                  if (end < meta.totalPages) {
                    if (end < meta.totalPages - 1) {
                      pages.push(
                        <span key="ellipsis-end" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                    pages.push(
                      <button
                        key={meta.totalPages}
                        onClick={() => handlePageChange(meta.totalPages)}
                        className="w-9 h-9 flex items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        {meta.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              {/* Botón página siguiente */}
              <button
                onClick={handleNextPage}
                disabled={!meta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  meta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página siguiente"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Botón última página */}
              <button
                onClick={() => handlePageChange(meta.totalPages)}
                disabled={!meta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  meta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Última página"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer de detalles del rol */}
      <RoleDetailsDrawer
        isOpen={selectedRole !== null}
        role={selectedRole}
        onClose={handleCloseRoleDetails}
        onEditRole={handleEditRole}
        onToggleRoleStatus={handleToggleRoleStatus}
      />

      {/* Drawer de crear rol */}
      <CreateRoleDrawer
        isOpen={isCreateRoleOpen}
        onClose={handleCloseCreateRole}
        onSuccess={handleCreateRoleSuccess}
      />

      {/* Drawer de editar rol */}
      <EditRoleDrawer
        isOpen={roleToEdit !== null}
        role={roleToEdit}
        onClose={handleCloseEditRole}
        onSuccess={() => refetchRoles()}
        onUpdateResult={handleUpdateRoleSuccess}
      />

      {/* Notification Toast para actualizaciones */}
      {updateNotificationConfig && (
        <NotificationToast
          isOpen={showUpdateNotification}
          title={updateNotificationConfig.title}
          message={updateNotificationConfig.message}
          type={updateNotificationConfig.type}
          onClose={() => {
            setShowUpdateNotification(false);
            setUpdateNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default RolesPage;